import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, X, Link2 } from 'lucide-react';

const LinkedClassGroupForm = ({ group, onSave, onCancel }) => {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [capacityLimit, setCapacityLimit] = useState(group?.capacity_limit || '');
  const [members, setMembers] = useState(group?.members || []);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAvailableClasses();
  }, []);

  const fetchAvailableClasses = async () => {
    const { data } = await supabase
      .from('classes')
      .select('id, name, start_time, end_time, enrollment_count, capacity')
      .eq('status', 'active')
      .order('name');
    
    setAvailableClasses(data || []);
  };

  const handleAddMember = (classId, linkType) => {
    const classInfo = availableClasses.find(c => c.id === classId);
    if (!classInfo) return;

    setMembers([...members, {
      class_id: classId,
      link_type: linkType,
      display_name: linkType === 'extended' ? 'Extended Care' : classInfo.name,
      class: classInfo
    }]);
  };

  const handleRemoveMember = (index) => {
    setMembers(members.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Save group
      const groupData = {
        name,
        description,
        capacity_limit: capacityLimit ? parseInt(capacityLimit) : null
      };

      let groupId = group?.id;

      if (group?.id) {
        await supabase
          .from('class_link_groups')
          .update(groupData)
          .eq('id', group.id);
      } else {
        const { data } = await supabase
          .from('class_link_groups')
          .insert([groupData])
          .select()
          .single();
        groupId = data.id;
      }

      // Save members
      for (const member of members) {
        if (!member.id) {
          await supabase.from('class_link_members').insert([{
            group_id: groupId,
            class_id: member.class_id,
            link_type: member.link_type,
            display_name: member.display_name
          }]);
        }
      }

      onSave();
    } catch (error) {
      console.error('Error saving linked class group:', error);
      alert('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="form-label">Group Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="form-input"
          placeholder="e.g., Explorers Club 2026-27"
          required
        />
      </div>

      <div>
        <label className="form-label">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="form-input"
          rows={2}
          placeholder="Optional description of this program"
        />
      </div>

      <div>
        <label className="form-label">Total Capacity Limit (Optional)</label>
        <input
          type="number"
          value={capacityLimit}
          onChange={(e) => setCapacityLimit(e.target.value)}
          className="form-input"
          placeholder="Max students across all linked classes"
        />
        <p className="text-sm text-gray-500 mt-1">
          Leave empty for no limit
        </p>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-semibold mb-3 flex items-center">
          <Link2 size={18} className="mr-2" />
          Linked Classes
        </h4>

        {members.length === 0 && (
          <p className="text-gray-500 text-sm mb-3">No classes added yet</p>
        )}

        <div className="space-y-3 mb-4">
          {members.map((member, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className={`badge ${
                  member.link_type === 'primary' ? 'bg-purple-100 text-purple-800' :
                  member.link_type === 'extended' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {member.link_type}
                </span>
                <div>
                  <p className="font-medium">{member.class?.name}</p>
                  <p className="text-sm text-gray-500">
                    {member.class?.start_time} - {member.class?.end_time}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveMember(index)}
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex space-x-2">
          <select
            id="addClassSelect"
            className="form-input flex-1"
            defaultValue=""
          >
            <option value="" disabled>Select a class to add...</option>
            {availableClasses
              .filter(c => !members.find(m => m.class_id === c.id))
              .map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.start_time}-{c.end_time})
                </option>
              ))}
          </select>
          <select
            id="linkTypeSelect"
            className="form-input w-32"
            defaultValue="primary"
          >
            <option value="primary">Primary</option>
            <option value="extended">Extended</option>
            <option value="addon">Add-on</option>
          </select>
          <button
            type="button"
            onClick={() => {
              const classId = document.getElementById('addClassSelect').value;
              const linkType = document.getElementById('linkTypeSelect').value;
              if (classId) handleAddMember(classId, linkType);
            }}
            className="btn btn-secondary"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      <div className="flex space-x-3 pt-4 border-t">
        <button
          type="submit"
          disabled={loading || members.length === 0}
          className="btn btn-primary"
        >
          {loading ? 'Saving...' : (group ? 'Update Group' : 'Create Group')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-ghost"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default LinkedClassGroupForm;
